import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Card, CardHeader, Collapse, Grid, IconButton, Input, TextField } from '@mui/material';
import ErrorFallback from '~/components/ErrorFallback';
import Layout from '~/components/Layout';
import { useLang } from '~/hooks/useLang';
import notify from '~/utils/notify';
import { useHookstate } from '@hookstate/core';
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies';
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups';
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch';
import CancelIcon from '@mui/icons-material/Cancel';
// import { makeRequestTogetReportOccurrencesByCommander } from './services';
import useReportState from '~/features/Report/stores/ReportState';
import { downloadReportCSV } from '~/features/Report/services/report';
import ExportButtonCSV from '~/features/Report/components/ButtonExportCSV';
import GenerateReportButton from '~/features/Report/components/GenerateReportButton';
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet';
import InputMask from 'react-input-mask';
import { makeRequestTogetReportDetailedApproachesByUser } from './services';
import { removeMask } from '~/features/Dispatch/utils/stringsManipulations';
import { validPlate } from '~/utils/validators';

// eslint-disable-next-line max-lines-per-function
export default function DetailedApproachesByUser() {
  const { translate } = useLang();
  const expandFilterSearch = useHookstate<boolean>(true);
  const isLoading = useHookstate<boolean>(false);
  const lockFields = useHookstate<boolean>(false);
  const startDate = useHookstate<string | null>(null);
  const finishDate = useHookstate<string | null>(null);
  const period = useHookstate('0');
  const agencyIds = useHookstate<readonly number[]>([]);
  const groupIds = useHookstate<readonly number[]>([]);
  const groupSetId = useHookstate<number | null>(null)
  const userId = useHookstate<number | null>(null);
  const Input = InputMask as any;
  const plate = useHookstate<string | null>(null);
  const cpf = useHookstate<string | null>(null);
  const imei = useHookstate('');
  const resetValeus = useHookstate(false);
  const labelButton = useHookstate('');


  const reportName = 'Detailed approaches by user'

  const { reportData, loadingReport } = useReportState();

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  const verifyCPF = () => {
    if (!cpf.get()) {
      return true
    } else if (cpf.get()) {
      return removeMask(cpf.get()).length == 11 ? true : false
    }
  }

  const verifyPlate = () => {
    if (!plate.get()) {
      return true
    } else if (plate.get()) {
      return validPlate(String(plate.get()))
    }
  }


  async function generateReport(exportCSV = false) {
    if ((startDate && finishDate) && verifyCPF() && verifyPlate()) {
      loadingReport().set(true)
      isLoading.set(true)
      const data = await makeRequestTogetReportDetailedApproachesByUser({
        startDate: startDate.get(),
        finishDate: finishDate.get(),
        agencyIds: agencyIds.get(),
        dispatchGroupsIds: groupIds.get(),
        groupSetId: groupSetId.get(),
        userId: userId.get(),
        cpf: removeMask(cpf.get()),
        imei: imei.get(),
        plate: plate.get(),
        exportCSV: exportCSV,
      }).finally(() => {
        loadingReport().set(false)
        isLoading.set(false)
      })
      if (exportCSV) {
        downloadReportCSV(
          `${translate(reportName)}-${new Date().getTime()}.csv`,
          data
        )
      } else {
        setTimeout(() => {
          reportData().set(data)
        })
        window.open(`/report/result/${reportName}`)
      }
    } else {
      !verifyPlate() ? notify({ message: translate('Invalid plate'), type: 'error' }) : false
      !verifyCPF() ? notify({ message: translate('Invalid CPF'), type: 'error' }) : false
      !startDate.get() && !finishDate.get() ? notify({ message: translate('Initial and final date are mandatory'), type: 'error' }) : false
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true} >
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader title={`${translate('Report')}: ${translate(`Detailed approaches by user`)}`} />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader title={`${translate('Filters')}`}
            avatar={(
              <IconButton
                onClick={() => expandFilterSearch.set(!expandFilterSearch.get())}
                sx={{ display: 'flex', justifyContent: 'end', marginRight: 1, padding: 0 }}>
                {expandFilterSearch.value ? (<ExpandLessIcon />) : (<ExpandMoreIcon />)}
              </IconButton>
            )}
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'row'} rowGap={2}>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteFetch
                    label={'User'}
                    urlFetch={'/user/get'}
                    size={"small"}
                    disabled={lockFields.get()}
                    onFetchChange={(user: number | null) => userId.set(user)}
                    resetValues={resetValeus.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchsGroups
                    disabled={lockFields.get()}
                    agencyId={agencyIds.get() ?? undefined}
                    dispatcheGroups={groupIds.get() ?? undefined}
                    onGroupDispatchChange={(group) => {
                      groupIds.set(group)
                      // deviceId.set(null)
                      labelButton.set(translate('Search'))
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchGroupSet
                    disabled={lockFields.get()}
                    dispatchGroupSetId={groupSetId.get() ?? undefined}
                    onGroupDispatchSetChange={(group) => {
                      groupSetId.set(group)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteAgencies
                    disabled={lockFields.get()}
                    agenciesId={agencyIds.get()}
                    onAgenciesChange={(agency) => {
                      labelButton.set(translate('Search'))
                      agencyIds.set(agency)
                    }} />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <TextField
                    fullWidth
                    inputProps={{ maxLength: 7 }}
                    label={`${translate('Plate')}`}
                    size="small"
                    value={plate.get() ?? ''}
                    onChange={(event) => { plate.set(event.target.value) }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <Input
                    mask="999.999.999-99"
                    value={cpf.get() ?? ''}
                    onChange={(event) => {
                      cpf.set(event.target.value)
                    }}
                  >
                    <TextField
                      fullWidth
                      label={`${translate('CPF')}`}
                      size="small"
                    />
                  </Input>
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <TextField fullWidth
                    id="imei"
                    label='Imei'
                    name="imei"
                    size='small'
                    value={imei.get()}
                    onChange={(event) => {
                      imei.set(event.target.value)
                    }}
                  />
                </Grid>
                  <FilterSelectPeriod
                    disabled={lockFields.get()}
                    onStartDateChange={(value) => startDate.set(value?.toJSON() ?? null)}
                    onFinishDateChange={(value) => finishDate.set(value?.toJSON() ?? null)}
                    onPeriodChange={(value) => { period.set(value) }}
                    period={period.get()}
                  />
              </Grid>
              <Grid item
                xs={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  pt: 2
                }}>
                <Button variant="outlined"
                  onClick={() => {
                    startDate.set(null)
                    finishDate.set(null)
                    agencyIds.set([])
                    groupIds.set([])
                    groupSetId.set(null)
                    userId.set(null)
                    cpf.set(null)
                    imei.set('')
                    plate.set(null)
                    period.set('0')
                  }}
                  sx={{ height: '40px' }}
                >
                  {translate('Clear Filters')}
                </Button>
                <ExportButtonCSV
                  isLoading={isLoading.get()}
                  dataToExport={() => generateReport(true)}
                />
                <GenerateReportButton
                  isLoading={isLoading.get()}
                  makeReport={() => generateReport()}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary >
  )
}